import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaChevronDown } from "react-icons/fa6";
import { BsChatDotsFill, BsGearFill, BsShieldFillCheck } from "react-icons/bs";
import { FiUser, FiMail, FiChevronDown, FiArrowRight } from "react-icons/fi";
import { Container, SectionHeading, Badge } from "./ui";
import homeFaqs from "./homeFaqs";

const MAILER_URL = "https://mailer.datametricks.com/send-email.php";

const numberTones = [
  "bg-blue-50 text-blue-600",
  "bg-violet-50 text-violet-600",
  "bg-emerald-50 text-emerald-600",
  "bg-orange-50 text-orange-500",
  "bg-fuchsia-50 text-fuchsia-600",
  "bg-blue-50 text-blue-600",
  "bg-emerald-50 text-emerald-600",
  "bg-violet-50 text-violet-600",
];

const projectTypes = [
  "Website Development",
  "E-commerce Development",
  "Custom Web Application",
  "UI/UX Design",
  "CMS Development",
  "Mobile App Development",
  "Digital Marketing / SEO",
  "Maintenance & Support",
  "Other",
];

const FaqItem = ({ i, item, open, onToggle }) => (
  <div className={`rounded-2xl bg-white ring-1 transition ${open ? "ring-blue-200 shadow-card" : "ring-slate-100"}`}>
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      className="flex w-full items-center gap-[14px] border-0 bg-transparent px-[18px] py-[16px] text-left"
    >
      <span className={`inline-flex h-[32px] min-w-[38px] items-center justify-center rounded-lg text-[13px] font-bold ${numberTones[i % numberTones.length]}`}>
        {String(i + 1).padStart(2, "0")}
      </span>
      <span className="flex-1 text-[15px] font-semibold text-ink">{item.q}</span>
      <FaChevronDown className={`shrink-0 text-[13px] text-slate-500 transition ${open ? "rotate-180" : ""}`} />
    </button>
    <div className={`grid transition-all duration-300 ${open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
      <div className="overflow-hidden">
        <p className="px-[18px] pb-[18px] pl-[70px] text-[14px] leading-relaxed text-slate-500">{item.a}</p>
      </div>
    </div>
  </div>
);

const inputCls =
  "w-full rounded-[10px] border-[1px] border-solid border-[#DCE3EF] bg-white text-[14px] text-ink outline-none transition placeholder:text-slate-400 focus:border-[#3D6EF7] focus:ring-4 focus:ring-blue-100";

const ContactForm = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", projectType: "", message: "" });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please enter your name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Please enter a valid email address.";
    if (!form.projectType) e.projectType = "Please select a project type.";
    if (form.message.trim().length < 10) e.message = "Please enter at least 10 characters.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    setStatus("");
    try {
      const res = await fetch(MAILER_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          phone: "N/A",
          message: `Project Type: ${form.projectType}\n\n${form.message}`,
        }),
      });
      const result = await res.json();
      if (result.status === "success") {
        navigate("/Successemailpage");
        return;
      }
      setStatus(result.message || "Something went wrong. Please try again.");
    } catch (err) {
      setStatus("Error submitting form. Please try again.");
    }
    setLoading(false);
  };

  const Err = ({ k }) => (errors[k] ? <p className="mt-1 text-[12px] text-rose-500">{errors[k]}</p> : null);
  const Label = ({ htmlFor, children }) => (
    <label htmlFor={htmlFor} className="mb-[8px] block text-[14px] font-medium text-ink">
      {children} <span className="text-rose-500">*</span>
    </label>
  );

  return (
    <form
      onSubmit={onSubmit}
      noValidate
      className="relative rounded-[20px] border-[1px] border-solid border-[#E6ECF8] bg-white p-[22px] shadow-[0_18px_50px_-24px_rgba(40,60,140,0.25)] sm:p-[26px]"
    >
      <h3 className="text-[20px] font-bold text-ink sm:text-[21px]">Get a Free Consultation</h3>
      <p className="mt-[4px] text-[14.5px] text-slate-500">Tell us about your project and we&apos;ll get back to you shortly.</p>

      <div className="mt-[18px] grid gap-x-[22px] gap-y-[14px] sm:grid-cols-2">
        <div>
          <Label htmlFor="cf-name">Your Name</Label>
          <div className="relative">
            <FiUser className="pointer-events-none absolute left-[16px] top-1/2 -translate-y-1/2 text-[16px] text-slate-500" />
            <input
              id="cf-name"
              name="name"
              value={form.name}
              onChange={onChange}
              placeholder="John Doe"
              className={`${inputCls} h-[46px] pl-[44px] pr-[16px]`}
            />
          </div>
          <Err k="name" />
        </div>
        <div>
          <Label htmlFor="cf-email">Your Email</Label>
          <div className="relative">
            <FiMail className="pointer-events-none absolute left-[16px] top-1/2 -translate-y-1/2 text-[16px] text-slate-500" />
            <input
              id="cf-email"
              name="email"
              type="email"
              value={form.email}
              onChange={onChange}
              placeholder="john@example.com"
              className={`${inputCls} h-[46px] pl-[44px] pr-[16px]`}
            />
          </div>
          <Err k="email" />
        </div>
        <div>
          <Label htmlFor="cf-type">Your Project Type</Label>
          <div className="relative">
            <select
              id="cf-type"
              name="projectType"
              value={form.projectType}
              onChange={onChange}
              className={`${inputCls} h-[46px] cursor-pointer appearance-none pl-[16px] pr-[40px] ${form.projectType ? "" : "text-slate-400"}`}
            >
              <option value="">Select a service</option>
              {projectTypes.map((p) => (
                <option key={p} style={{ color: "#0B0D2C" }}>
                  {p}
                </option>
              ))}
            </select>
            <FiChevronDown className="pointer-events-none absolute right-[16px] top-1/2 -translate-y-1/2 text-[16px] text-slate-500" />
          </div>
          <Err k="projectType" />
        </div>
        <div>
          <Label htmlFor="cf-message">Your Message</Label>
          <textarea
            id="cf-message"
            name="message"
            rows={2}
            value={form.message}
            onChange={onChange}
            placeholder="Tell us about your project..."
            className={`${inputCls} block h-[56px] min-h-[46px] resize-y px-[16px] py-[10px] leading-snug`}
          />
          <Err k="message" />
        </div>
      </div>

      <button
        type="submit"
        disabled={loading}
        className="group mt-[20px] flex h-[48px] w-full items-center justify-center gap-[12px] rounded-full border-0 bg-gradient-to-r from-[#1E6BFF] via-[#5B3DF5] to-[#9B1FF0] text-[16px] font-medium text-white shadow-[0_12px_28px_-12px_rgba(91,61,245,0.6)] transition hover:brightness-110 disabled:opacity-60"
      >
        {loading ? "Sending..." : "Send Message"}
        <FiArrowRight className="text-[18px] transition group-hover:translate-x-1" />
      </button>
      {status && <p className="mt-[12px] text-center text-[13px] text-rose-500">{status}</p>}
    </form>
  );
};

const perks = [
  { icon: BsChatDotsFill, label: ["Quick", "Response"], tone: "bg-[#E8F1FF] text-[#1E6BFF]" },
  { icon: BsGearFill, label: ["Tailored", "Solution"], tone: "bg-[#F3E8FF] text-[#9333EA]" },
  { icon: BsShieldFillCheck, label: ["On-Time", "Delivery"], tone: "bg-[#E6F8EE] text-[#16A34A]" },
];

// Decorative curly arrow pointing at the form.
const CurlyArrow = () => (
  <svg viewBox="0 0 150 70" fill="none" className="h-[62px] w-[140px]" aria-hidden="true">
    <path
      d="M4 40 C 30 10, 70 2, 92 22 C 108 38, 86 56, 76 40 C 66 22, 100 14, 140 46"
      stroke="#8B2CF5"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <path d="M128 46 L141 47 L136 35" stroke="#8B2CF5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const DiscussNote = () => (
  <div className="relative">
    <span className="absolute -top-[22px] left-[52px] flex gap-[6px]" aria-hidden="true">
      <span className="h-[12px] w-[3px] -rotate-[20deg] rounded-full bg-[#38A3F5]" />
      <span className="h-[14px] w-[3px] rounded-full bg-[#38A3F5]" />
      <span className="mt-[6px] h-[3px] w-[12px] -rotate-[20deg] rounded-full bg-[#38A3F5]" />
    </span>
    <div className="-rotate-[14deg] whitespace-nowrap rounded-[16px] bg-gradient-to-br from-[#F4EEFF] to-[#ECE6FF] px-[16px] py-[14px] text-[13px] font-medium leading-[1.35] text-ink shadow-[0_10px_24px_-12px_rgba(91,61,245,0.35)]">
      Let&apos;s
      <br />
      Discuss
      <br />
      Your Ideas
    </div>
  </div>
);

// `faqs` lets other pages show their own questions; defaults to the home page FAQs.
const FaqContact = ({ faqs = homeFaqs }) => {
  const [open, setOpen] = useState(0);
  const half = Math.ceil(faqs.length / 2);
  const left = faqs.slice(0, half);
  const right = faqs.slice(half);

  const column = (items, offset) => (
    <div className="flex flex-col gap-[14px]">
      {items.map((item, idx) => {
        const i = idx + offset;
        return <FaqItem key={item.q} i={i} item={item} open={open === i} onToggle={() => setOpen(open === i ? -1 : i)} />;
      })}
    </div>
  );

  return (
    <section id="contact" className="bg-white py-[32px] lg:py-[46px]">
      <Container>
        <SectionHeading
          badge="FAQ"
          lined
          title="Frequently Asked"
          highlight="Questions"
          highlightBreak={false}
          subtitle="Find quick answers to common questions about our services, process, and support."
        />
        <div className="mt-[40px] grid items-start gap-[14px] lg:grid-cols-2">
          {column(left, 0)}
          {column(right, half)}
        </div>

      </Container>

      {/* CTA box is a little wider than the FAQ grid, like the design */}
      <div className="mx-auto w-full max-w-[1400px] px-[16px] sm:px-6 lg:px-8">
        <div className="mt-[56px] grid items-center gap-[32px] rounded-[28px] border-[1px] border-solid border-[#E3EAF8] bg-gradient-to-br from-[#FBFCFF] via-white to-[#F8F7FF] p-[22px] shadow-[0_20px_60px_-36px_rgba(40,60,140,0.25)] sm:p-[36px] lg:grid-cols-[1fr_minmax(0,1.05fr)] xl:grid-cols-[minmax(0,1fr)_100px_minmax(0,560px)] 2xl:grid-cols-[minmax(0,1fr)_120px_minmax(0,600px)] xl:gap-[16px] xl:px-[40px]">
          <div>
            <Badge>Let&apos;s work together</Badge>
            <h2 className="mt-[18px] text-[32px] font-bold leading-[1.12] tracking-tight text-ink sm:text-[40px] xl:text-[44px]">
              Ready to Build
              <br />
              <span className="text-gradient">Your Next Project?</span>
            </h2>
            <p className="mt-[14px] max-w-[500px] text-[16px] leading-[1.55] text-slate-600 sm:text-[17px]">
              Share your ideas with us and let&apos;s create a digital solution that helps your business grow.
            </p>
            {/* Always one row of three; sizes shrink with the screen instead of wrapping */}
            <div className="mt-[28px] grid max-w-[560px] grid-cols-3">
              {perks.map((p, i) => (
                <div
                  key={p.label.join(" ")}
                  className={`flex min-w-0 flex-col items-start gap-[8px] pr-[8px] sm:flex-row sm:items-center sm:gap-[clamp(8px,0.8vw,12px)] sm:pr-[clamp(8px,1vw,16px)] ${
                    i > 0 ? "border-y-0 border-l-[1px] border-r-0 border-solid border-slate-200 pl-[10px] sm:pl-[clamp(10px,1.1vw,18px)]" : ""
                  }`}
                >
                  <span
                    className={`inline-flex h-[44px] w-[44px] shrink-0 items-center justify-center rounded-full text-[18px] sm:h-[clamp(44px,3.4vw,54px)] sm:w-[clamp(44px,3.4vw,54px)] sm:text-[clamp(17px,1.3vw,21px)] ${p.tone}`}
                  >
                    <p.icon />
                  </span>
                  <span className="whitespace-nowrap text-[13px] font-medium leading-[1.4] text-ink sm:text-[clamp(13px,0.95vw,15px)]">
                    {p.label[0]}
                    <br />
                    {p.label[1]}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Decorative arrow + note (wide screens only) */}
          <div className="hidden h-full flex-col items-center justify-between py-[8px] xl:flex">
            <div className="-ml-[60px]">
              <CurlyArrow />
            </div>
            <div className="mb-[40px]">
              <DiscussNote />
            </div>
          </div>

          <ContactForm />
        </div>
      </div>
    </section>
  );
};

export default FaqContact;
